import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '../brokerPortalV2/common_v2/components/Footer';
import { alertModal } from '@apply/ui';
import { Button, ButtonTheme, EmailBox, Panel, PanelTheme, PanelBorderRadius } from '@apply/components';
import { isMobileScreen } from '@apply/ui';
import { LogoLink } from '../common/components/LogoLink';
import { LoginApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';

export const ForgotPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const mobileScreen = isMobileScreen();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const requestedEmail = queryParams.get('email');

    const [email, setEmail] = useState<string>(requestedEmail ?? '');
    const [isEmailValid, set_isEmailValid] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);

    const handleRequestForgotPassword = async () => {
        if (!email) {
            return;
        }

        setSubmitting(true);

        try {
            const loginApi = new LoginApi.LoginApi(applicationInsights);
            const response = await loginApi.forgotPassword({ email: email });
            setSubmitting(false);
            if (response && response.statusCode === 200) {
                setSuccessful(true);
            }
        } catch (err) {
            setSubmitting(false);
            setSuccessful(false);
            showError(err?.message);
        }
    };

    const readyToSubmit = () => {
        return !submitting && isEmailValid;
    };

    const onEnterKey = () => {
        // only submit if both fields valid
        if (readyToSubmit()) {
            handleRequestForgotPassword();
        }
    };

    const showError = (msg?: string | null | undefined) => {
        alertModal({
            message: msg ?? 'An error has occurred while communicating with our server.  Please try your operation again.',
        });
    };

    const onChangeEmail = (email: string) => {
        setEmail(email);
        setSubmitting(false);
    };

    const renderPasswordResetContent = () => {
        return (
            <>
                <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                    <LogoLink />
                </div>
                <main>
                    <h2>Request a password reset</h2>
                    <div className='passwordReset subheader'>
                        Please enter your email address.
                        <br />
                        We will send you an email to reset your password.
                    </div>
                    <EmailBox
                        id='emailAddress'
                        dataTestid='forgot-password-email-address'
                        name='emailAddress'
                        value={email}
                        onChange={onChangeEmail}
                        onEnterKey={onEnterKey}
                        onValidationEvent={(isValid) => set_isEmailValid(isValid)}
                        label='Email Address'
                        required={true}
                    />
                    <div className='passwordReset primaryActions'>
                        <Button
                            id='forgot-password-button'
                            dataTestid='forgot-password-button'
                            key='forgot-password-button'
                            width='80%'
                            theme={ButtonTheme.Primary}
                            disabled={!readyToSubmit()}
                            handleClick={handleRequestForgotPassword}
                        >
                            Reset Password
                        </Button>
                    </div>
                    <div className='passwordReset secondaryActions'>
                        <div style={{ float: 'left', width: '100%' }}>
                            <Button
                                id='login-button'
                                dataTestid='navigate-login-button'
                                key='login-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/login')}
                            >
                                Return to Log In
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    const renderSuccessContent = () => {
        return (
            <>
                <main>
                    <div style={{ textAlign: 'center' }}>
                        <h2>Your request has been processed</h2>
                        <div style={{ paddingTop: '2vh', paddingBottom: '2vh' }}>
                            We sent a link to <b>{email}</b> if it is registered in the system. <br />
                            <br />
                            Please check your email and click the link in the email to reset your password.
                        </div>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Button
                                id='login-button'
                                dataTestid='navigate-login-button'
                                key='login-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/login')}
                                width='200px'
                            >
                                Return to Log In
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    if (successful) {
        return (
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                {renderSuccessContent()}
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderSuccessContent()}</div>
                    )}
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>
                                    {renderPasswordResetContent()}
                                </div>
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderPasswordResetContent()}</div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
